import { requiredElement } from "../shared/dom";
import type { ReviewHotspot } from "../shared/reviewScene";

export interface ReviewTourCallbacks {
  onStep(hotspot: ReviewHotspot): void;
  onClose(): void;
}

export class ReviewTour {
  #card: HTMLElement;
  #progress: HTMLElement;
  #title: HTMLElement;
  #description: HTMLElement;
  #back: HTMLButtonElement;
  #next: HTMLButtonElement;
  #close: HTMLButtonElement;
  #hotspots: readonly ReviewHotspot[] = [];
  #index = 0;
  #callbacks: ReviewTourCallbacks;

  constructor(card: HTMLElement, callbacks: ReviewTourCallbacks) {
    this.#card = card;
    this.#callbacks = callbacks;
    this.#progress = requiredElement("#tourProgress", card);
    this.#title = requiredElement("#tourTitle", card);
    this.#description = requiredElement("#tourDescription", card);
    this.#back = requiredElement("#tourBackButton", card);
    this.#next = requiredElement("#tourNextButton", card);
    this.#close = requiredElement("#tourCloseButton", card);
    this.#back.addEventListener("click", () => this.#setIndex(this.#index - 1));
    this.#next.addEventListener("click", () => {
      if (this.#index === this.#hotspots.length - 1) this.close();
      else this.#setIndex(this.#index + 1);
    });
    this.#close.addEventListener("click", () => this.close());
  }

  setHotspots(hotspots: readonly ReviewHotspot[]): void {
    this.#hotspots = hotspots;
  }

  start(): void {
    if (!this.#hotspots.length) return;
    this.#card.hidden = false;
    this.#setIndex(0);
    this.#close.focus({ preventScroll: true });
  }

  close(): void {
    if (this.#card.hidden) return;
    this.#card.hidden = true;
    this.#callbacks.onClose();
  }

  #setIndex(index: number): void {
    if (!this.#hotspots.length) return;
    this.#index = Math.max(0, Math.min(index, this.#hotspots.length - 1));
    const hotspot = this.#hotspots[this.#index]!;
    this.#progress.textContent = `${this.#index + 1} of ${this.#hotspots.length}`;
    this.#title.textContent = hotspot.label;
    this.#description.textContent = hotspot.description;
    this.#back.disabled = this.#index === 0;
    this.#next.textContent = this.#index === this.#hotspots.length - 1 ? "Finish" : "Next";
    this.#callbacks.onStep(hotspot);
  }
}
